import { logWarn, logError, logDebug } from './utils/logger';

const THIRTY_MINUTES = 30 * 60 * 1000;
const KEEP_ALIVE_THRESHOLD = 64 * 1024 * 0.8; // 64KB * 0.8 for safety margin

/**
 * Generates a jittered exponential backoff delay in milliseconds
 * 
 * The base value is 3 seconds, which is doubled with each retry
 * up to the maximum of 30 minutes
 * 
 * Each value then has +/- 50% jitter
 * 
 * Giving a range of 3 seconds up to 45 minutes
 */
export function pickNextRetryDelay(retriesPerformedSoFar: number): number {
    const rawBackoffTime = 3000 * 2 ** retriesPerformedSoFar;
    const minBackoff = rawBackoffTime / 2;
    const cappedBackoffTime = Math.min(THIRTY_MINUTES, rawBackoffTime);
    const jitterFraction = Math.random() - 0.5; // A random number between -0.5 and 0.5
    const jitter = jitterFraction * (cappedBackoffTime - minBackoff);
    return Math.ceil(cappedBackoffTime + jitter);
}

export interface RetriableRequestOptions {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string | Blob;
    retriesPerformedSoFar?: number;
    estimatedSize?: number;
    callback?: (response: { statusCode: number; text: string; json?: any }) => void;
}

interface RetryQueueElement {
    retryAt: number;
    requestOptions: RetriableRequestOptions;
}

export class RetryQueue {
    private _isPolling: boolean = false;
    private _poller: ReturnType<typeof setTimeout> | undefined;
    private _pollIntervalMs: number = 3000;
    private _queue: RetryQueueElement[] = [];
    private _areWeOnline: boolean;
    private _sendRequest: (options: RetriableRequestOptions) => Promise<void>;

    constructor(sendRequest: (options: RetriableRequestOptions) => Promise<void>) {
        this._queue = [];
        this._areWeOnline = true;
        this._sendRequest = sendRequest;

        if (typeof window !== 'undefined' && 'onLine' in window.navigator) {
            this._areWeOnline = window.navigator.onLine;

            window.addEventListener('online', () => {
                this._areWeOnline = true;
                this._flush();
            });

            window.addEventListener('offline', () => {
                this._areWeOnline = false;
            });
        }
    }

    get length(): number {
        return this._queue.length;
    }

    async retriableRequest(options: RetriableRequestOptions): Promise<void> {
        const retriesPerformedSoFar = options.retriesPerformedSoFar || 0;
        
        // Add retry count to URL if retrying
        if (retriesPerformedSoFar > 0) {
            const url = new URL(options.url);
            url.searchParams.set('retry_count', retriesPerformedSoFar.toString());
            options.url = url.toString();
        }

        try {
            await this._sendRequest(options);
        } catch (error: any) {
            // Check if we should retry
            const shouldRetry = this._shouldRetry(error, retriesPerformedSoFar);
            
            if (shouldRetry && retriesPerformedSoFar < 10) {
                this._enqueue(options);
                return;
            }

            // Call callback with error if provided
            if (options.callback) {
                options.callback({
                    statusCode: error.status || 0,
                    text: error.message || 'Request failed'
                });
            }
        }
    }

    private _shouldRetry(error: any, retriesPerformedSoFar: number): boolean {
        // Don't retry on client errors (4xx) except for 408, 429
        if (error.status >= 400 && error.status < 500) {
            return error.status === 408 || error.status === 429;
        }
        
        // Retry on server errors (5xx) and network errors
        return error.status >= 500 || !error.status;
    }

    private _enqueue(requestOptions: RetriableRequestOptions): void {
        const retriesPerformedSoFar = requestOptions.retriesPerformedSoFar || 0;
        requestOptions.retriesPerformedSoFar = retriesPerformedSoFar + 1;

        const msToNextRetry = pickNextRetryDelay(retriesPerformedSoFar);
        const retryAt = Date.now() + msToNextRetry;

        this._queue.push({ retryAt, requestOptions });

        let logMessage = `Enqueued failed request for retry in ${Math.round(msToNextRetry / 1000)}s`;
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            logMessage += ' (Browser is offline)';
        }
        logWarn(logMessage);

        if (!this._isPolling) {
            this._isPolling = true;
            this._poll();
        }
    }

    private _poll(): void {
        if (this._poller) {
            clearTimeout(this._poller);
        }
        this._poller = setTimeout(() => {
            if (this._areWeOnline && this._queue.length > 0) {
                this._flush();
            }
            this._poll();
        }, this._pollIntervalMs);
    }

    private _flush(): void {
        const now = Date.now();
        const notToFlush: RetryQueueElement[] = [];
        const toFlush = this._queue.filter((item) => {
            if (item.retryAt < now) {
                return true;
            }
            notToFlush.push(item);
            return false;
        });

        this._queue = notToFlush;

        if (toFlush.length > 0) {
            for (const { requestOptions } of toFlush) {
                this.retriableRequest(requestOptions).catch((error) => {
                    logError('Failed to retry request:', error);
                });
            }
        }
    }

    unload(): void {
        if (this._poller) {
            clearTimeout(this._poller);
            this._poller = undefined;
        }

        for (const { requestOptions } of this._queue) {
            try {
                // Use sendBeacon for unload to ensure requests are sent
                this._sendBeaconRequest(requestOptions);
            } catch (e) {
                logError('Failed to send request via sendBeacon on unload:', e);
            }
        }
        this._queue = [];
    }

    private _sendBeaconRequest(options: RetriableRequestOptions): void {
        if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
            return;
        }

        try {
            const url = new URL(options.url);
            url.searchParams.set('beacon', '1');

            let body: Blob | null = null;
            if (options.body) {
                if (typeof options.body === 'string') {
                    // text/plain: CORS-simple; JSON blobs preflight and die on `*`.
                    body = new Blob([options.body], { type: 'text/plain' });
                } else if (options.body instanceof Blob) {
                    body = options.body;
                }
            }

            const success = navigator.sendBeacon(url.toString(), body);
            if (!success) {
                logWarn('sendBeacon returned false for unload request');
            }
        } catch (error) {
            logError('Error sending beacon request:', error);
        }
    }
}

